import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  ButtonBase,
  CircularProgress,
  Chip,
  Stack,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useMsal } from '@azure/msal-react';
import { Link as RouterLink } from 'react-router-dom';
import useLlmStream from './useLlmStream';
import {
  getOneClickCoverage,
  listOneClickFunctions,
  streamOneClickRun,
} from '../../helper/llmApi';
import OneClickResultDialog from './OneClickResultDialog';

function formatHours(seconds) {
  const s = Math.floor(Number(seconds) || 0);
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
}

function cacheKey(jobId) {
  return `oneclick-results:${jobId}`;
}

function readCache(jobId) {
  try {
    const raw = localStorage.getItem(cacheKey(jobId)) || sessionStorage.getItem(cacheKey(jobId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCache(jobId, value) {
  try {
    localStorage.setItem(cacheKey(jobId), JSON.stringify(value));
  } catch {
    sessionStorage.setItem(cacheKey(jobId), JSON.stringify(value));
  }
}

function safeName(value) {
  return (value || 'one-click-ergebnis')
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '');
}

function FunctionCard({ fn, onClick, disabled, cached }) {
  return (
    <ButtonBase
      onClick={() => onClick(fn)}
      disabled={disabled}
      sx={{
        width: '100%',
        borderRadius: 2,
        textAlign: 'left',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
          transition: 'border-color 120ms ease',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ fontSize: 28, lineHeight: 1 }}>{fn.icon || '✨'}</Box>
        <Box minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {fn.name}
            </Typography>
            {cached && <Chip size="small" label="Gespeichert" />}
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {fn.description || '\u00a0'}
          </Typography>
        </Box>
      </Paper>
    </ButtonBase>
  );
}

export default function OneClickPanel({ jobId }) {
  const { instance } = useMsal();
  const [functions, setFunctions] = useState({ global: [], user: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedFn, setSelectedFn] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cachedResults, setCachedResults] = useState({});
  const [openedFromCache, setOpenedFromCache] = useState(false);
  const [coverage, setCoverage] = useState(null);

  const { text, isStreaming, error, meta, run, abort, reset, setText, setMeta } = useLlmStream();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await listOneClickFunctions(instance);
        if (!active) return;
        setFunctions({
          global: data?.global || [],
          user: data?.user || [],
        });
      } catch (e) {
        if (!active) return;
        setLoadError(e.message || 'Konnte 1-Click-Funktionen nicht laden');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [instance]);

  useEffect(() => {
    setCachedResults(readCache(jobId));
  }, [jobId]);

  // Vorab-Hinweis bei langen Aufnahmen. Das Backend rechnet mit derselben
  // Zerlegung und Grenze wie der eigentliche Lauf, Hinweis und Ergebnis passen
  // also zusammen. Ohne Angabe gibt es einfach keinen Hinweis.
  useEffect(() => {
    let active = true;
    setCoverage(null);
    getOneClickCoverage(instance, jobId)
      .then((data) => {
        if (active) setCoverage(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [instance, jobId]);

  const storeCachedResult = React.useCallback((fn, result) => {
    setCachedResults((prev) => {
      const next = { ...prev, [fn.id]: result };
      writeCache(jobId, next);
      return next;
    });
  }, [jobId]);

  useEffect(() => {
    if (!selectedFn || openedFromCache || isStreaming || error || !text) return;
    storeCachedResult(selectedFn, {
      text,
      meta,
      cachedAt: new Date().toISOString(),
    });
  }, [error, isStreaming, meta, openedFromCache, selectedFn, storeCachedResult, text]);

  const openCachedResult = (fn) => {
    const cached = cachedResults[fn.id];
    if (!cached) return false;
    setSelectedFn(fn);
    setOpenedFromCache(true);
    setText(cached.text || '');
    setMeta(cached.meta || null);
    setDialogOpen(true);
    return true;
  };

  const startRun = async (fn) => {
    setSelectedFn(fn);
    setOpenedFromCache(false);
    setDialogOpen(true);
    reset();
    await run(async ({ signal, onEvent }) => {
      await streamOneClickRun({
        instance,
        jobId,
        functionId: fn.id,
        onEvent,
        signal,
      });
    });
  };

  const runFunction = async (fn) => {
    if (openCachedResult(fn)) return;
    await startRun(fn);
  };

  const closeDialog = () => {
    if (isStreaming) abort();
    setDialogOpen(false);
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Typography color="error" variant="body2">{loadError}</Typography>
      ) : (
        <>
          {/* "etwa eine Stunde" gehoert zur Grenze in oneclick_pipeline._max_chunks */}
          {coverage?.truncated && (
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              <AlertTitle>Nur die erste Stunde wird ausgewertet</AlertTitle>
              Diese Aufnahme ist {formatHours(coverage.total_duration)} h lang. Die
              1-Click-Funktionen können derzeit höchstens etwa eine Stunde auswerten –
              mehr lässt die Hardware unseres Servers nicht zu. Ausgewertet werden daher
              nur die ersten {formatHours(coverage.evaluated_until)} h. Für Fragen zum
              weiteren Verlauf nutze den Reiter „Chat“.
            </Alert>
          )}
          {functions.global.length > 0 && (
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Vordefiniert
              </Typography>
              <Grid container spacing={1.5} mt={0.25}>
                {functions.global.map((fn) => (
                  <Grid item xs={12} sm={6} md={6} lg={6} key={fn.id}>
                    <FunctionCard
                      fn={fn}
                      onClick={runFunction}
                      disabled={isStreaming}
                      cached={Boolean(cachedResults[fn.id])}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography variant="overline" color="text.secondary">
                Eigene Funktionen
              </Typography>
              <Button
                size="small"
                component={RouterLink}
                to="/settings/oneclick"
                startIcon={<SettingsIcon />}
              >
                Verwalten
              </Button>
            </Stack>
            {functions.user.length === 0 ? (
              <Chip
                variant="outlined"
                size="small"
                label="Noch keine eigenen Funktionen angelegt."
              />
            ) : (
              <Grid container spacing={1.5}>
                {functions.user.map((fn) => (
                  <Grid item xs={12} sm={6} md={6} lg={6} key={fn.id}>
                    <FunctionCard
                      fn={fn}
                      onClick={runFunction}
                      disabled={isStreaming}
                      cached={Boolean(cachedResults[fn.id])}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </>
      )}

      <OneClickResultDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={selectedFn?.name || '1-Click-Aktion'}
        icon={selectedFn?.icon}
        text={text}
        isStreaming={isStreaming}
        error={error}
        meta={meta}
        cachedAt={cachedResults[selectedFn?.id]?.cachedAt}
        filename={`${safeName(selectedFn?.name)}-${jobId}.txt`}
        onAbort={abort}
        onRerun={selectedFn ? () => startRun(selectedFn) : null}
      />
    </Box>
  );
}
